import React, {
  useState,
  useContext,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import { getAssetTagsFromHub } from "../../../api/APIHelpers";
import {
  createTextColumn,
  visibleColumnsStorageKey,
} from "../../../common/components/DataGrid/DataGridHelper";
import { CodeLegendContext } from "../../../common/contexts/CodeLegendContext";
import { LookupValueContext } from "../../../common/contexts/LookupValueContext";
import {
  DataGrid,
  TextDisplayCell,
  SelectColumnId,
} from "../../../common/components/DataGrid";
import { readArrayFromStorage, optionalColumns } from "../../helpers/Helpers";
import {
  createOptionalColumnDefs,
  optionalColumnLabels,
} from "../../helpers/OptionalColumns";
import { extractApiErrorMessage } from "../../../common/helpers/ErrorUtils";
import {
  DialogContent,
  DialogActions,
  Backdrop,
  CircularProgress,
  Divider,
  Typography,
  TextField,
  Box,
  Paper,
  Autocomplete,
  Checkbox,
  IconButton,
  Tooltip,
  Alert,
} from "@mui/material";
import {
  CheckBoxOutlineBlank,
  CheckBox,
  FilterList,
} from "@mui/icons-material";
import DragModal from "../../../common/components/DragModal";
import { AddTagsFromHubConfirmation } from "./AddTagsFromHubConfirmation";
import { SelectCollisionMode, CollisionMode } from "../SelectCollisionMode";
import {
  isValueDifferentForOptionalColumn,
  overwriteTag,
} from "../../helpers/TagComparisonHelper";
import SearchAssetNumber from "./SearchAssetNumber";
import SelectDistro from "../SelectDistro";
import { useTheme } from "@mui/material/styles";
import _ from "lodash";

const icon = <CheckBoxOutlineBlank fontSize='small' />;
const checkedIcon = <CheckBox fontSize='small' />;

/** Cycles: all rows → paired conflicts only → no paired conflicts (includes unpaired). */
const TagTableFilterMode = {
  All: 0,
  ConflictsOnly: 1,
  NoConflictsOnly: 2,
};

const USER_FILTER_TOOLTIPS = {
  [TagTableFilterMode.All]:
    "Showing all tags. Click to show paired conflicts only.",
  [TagTableFilterMode.ConflictsOnly]:
    "Showing paired tags with hub differences. Click to show tags with no paired conflicts.",
  [TagTableFilterMode.NoConflictsOnly]:
    "Showing tags with no paired hub conflicts (includes tags with no hub match). Click to show all.",
};

const HUB_FILTER_TOOLTIPS = {
  [TagTableFilterMode.All]:
    "Showing all hub tags. Click to show paired conflicts only.",
  [TagTableFilterMode.ConflictsOnly]:
    "Showing hub tags that differ from current tags. Click to show tags with no paired conflicts.",
  [TagTableFilterMode.NoConflictsOnly]:
    "Showing hub-only tags and pairs with no differences. Click to show all.",
};

const conflictColor = "rgba(161, 17, 17, 0.9)";

export default function TagComparisonModal({
  setShowTagComparisonDialog,
  userTags,
  setUserTags,
  distro,
  setDistro,
  setDirtyTags,
  showTagComparisonDialog,
  currentCollisionMode,
  currentSetCollisionMode,
  currentSelectedColumns,
  currentSetSelectedColumns,
}) {
  const { codeLegends } = useContext(CodeLegendContext);
  const lookupValues = useContext(LookupValueContext);
  const theme = useTheme();

  const [assetLoading, setAssetLoading] = useState(false);
  const [tagIdentifierMap, setTagIdentifierMap] = useState(new Map()); // Type <number, {userTag: AssetTagPlusDTO, tagFromHub: AssetTagPlusDTO}>
  const [selectedHubTags, setSelectedHubTags] = useState(new Set()); // Type <number>
  const collisionMode = currentCollisionMode;
  const setCollisionMode = currentSetCollisionMode;
  const selectedColumns = currentSelectedColumns;
  const setSelectedColumns = currentSetSelectedColumns;
  const [lastAssetNumberSearched, setLastAssetNumberSearched] = useState("");
  const [userTableFilterMode, setUserTableFilterMode] = useState(
    TagTableFilterMode.All
  );
  const [hubTableFilterMode, setHubTableFilterMode] = useState(
    TagTableFilterMode.All
  );
  const [infoMessage, setInfoMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const noConflictColor = theme.palette.primary.main;
  const leftColumns = useMemo(
    () => [
      createTextColumn("TagName", "Tag Name", { editable: false }),
      createTextColumn("TagIdentifier", "Tag Identifier", { editable: false }),
      {
        id: "UniversalDescriptor",
        accessorKey: "UniversalDescriptor",
        header: (
          <>
            Universal
            <br /> Descriptor
          </>
        ),
        displayComponent: ({ row }) => (
          <TextDisplayCell value={row.UniversalDescriptor} />
        ),
        editable: false,
      },
    ],
    []
  );

  useEffect(() => {
    const duplicates = userTags
      .map((tag) => tag.TagIdentifier)
      .filter((tagIdentifier, index, self) =>
        self.includes(tagIdentifier, index + 1)
      );
    if (duplicates.length > 0) {
      setInfoMessage(
        `There are duplicate tags in the current tags with tag IDs: ${duplicates.join(", ")}. Loading tags from the hub and choosing to overwrite tags will replace only one of the duplicates.`
      );
    } else {
      setInfoMessage("");
    }
  }, [userTags]);

  const selectedOptionalDefs = useMemo(() => {
    const optionalColumnDefs = createOptionalColumnDefs(
      codeLegends,
      undefined,
      lookupValues
    );
    return selectedColumns
      .map((columnName) => optionalColumnDefs[columnName])
      .filter(Boolean)
      .map((col) => ({
        ...col,
        editable: false,
      }));
  }, [codeLegends, lookupValues, selectedColumns]);

  const columns = useMemo(
    () => [...leftColumns, ...selectedOptionalDefs],
    [leftColumns, selectedOptionalDefs]
  );

  const userTagsForGrid = useMemo(() => {
    if (userTags === null) {
      return null;
    }
    if (userTableFilterMode === TagTableFilterMode.All) {
      return userTags;
    }
    return userTags.filter((t) => {
      const tags = tagIdentifierMap.get(t.TagIdentifier);
      let hasConflict;
      if (!tags) {
        hasConflict = false;
      } else {
        const { userTag, tagFromHub } = tags;
        hasConflict = userTag && tagFromHub;
      }
      if (userTableFilterMode === TagTableFilterMode.ConflictsOnly) {
        return hasConflict;
      }
      return !hasConflict;
    });
  }, [userTags, tagIdentifierMap, userTableFilterMode]);

  const hubTagRows = useMemo(() => {
    const rows = Array.from(tagIdentifierMap.values()).map(
      (entry) => entry.tagFromHub
    );
    if (hubTableFilterMode === TagTableFilterMode.All) {
      return rows;
    }
    return rows.filter((t) => {
      const { userTag, tagFromHub } = tagIdentifierMap.get(t.TagIdentifier);
      const hasConflict = userTag && tagFromHub;
      if (hubTableFilterMode === TagTableFilterMode.ConflictsOnly) {
        return hasConflict;
      }
      return !hasConflict;
    });
  }, [tagIdentifierMap, hubTableFilterMode]);

  useEffect(() => {
    if (hubTagRows === null) {
      return;
    }
    const visibleIds = new Set(hubTagRows.map((r) => r.TagIdentifier));
    setSelectedHubTags((prev) => {
      const next = new Set([...prev].filter((id) => visibleIds.has(id)));
      if (next.size === prev.size && [...prev].every((id) => next.has(id))) {
        return prev;
      }
      return next;
    });
  }, [hubTagRows]);

  const commitAddSelectedTags = useCallback(() => {
    const tagIdsToOverwrite = Array.from(selectedHubTags).filter(
      (tagIdentifier) => {
        const { userTag } = tagIdentifierMap.get(tagIdentifier);
        return collisionMode === CollisionMode.Overwrite && userTag;
      }
    );
    const tagsToOverwriteIndices = tagIdsToOverwrite.map((tagIdentifier) =>
      userTags.findIndex((tag) => tag.TagIdentifier === tagIdentifier)
    );
    const tagIdsToSkip = Array.from(selectedHubTags).filter((tagIdentifier) => {
      const { userTag } = tagIdentifierMap.get(tagIdentifier);
      return collisionMode === CollisionMode.Skip && userTag;
    });
    const tagIdsToAdd = _.difference(
      Array.from(selectedHubTags),
      tagIdsToOverwrite,
      tagIdsToSkip
    );
    const userTagLength = userTags.length;
    setUserTags((prev) => {
      tagIdsToOverwrite.forEach((tagIdentifier, index) => {
        const { userTag, tagFromHub } = tagIdentifierMap.get(tagIdentifier);
        const newTag = overwriteTag(userTag, tagFromHub, distro);
        prev[tagsToOverwriteIndices[index]] = newTag;
      });
      return [
        ...prev,
        ...tagIdsToAdd.map(
          (tagIdentifier) => tagIdentifierMap.get(tagIdentifier).tagFromHub
        ),
      ];
    });
    setDirtyTags((prev) => {
      let dirtyTagsAsArray = Array.from(prev);
      return new Set(
        dirtyTagsAsArray.concat([
          ...tagIdsToAdd.map((_, index) => index + userTagLength),
          ...tagIdsToOverwrite.map((_, index) => tagsToOverwriteIndices[index]),
        ])
      );
    });
    setTagIdentifierMap((prev) => {
      const next = new Map(prev);
      tagIdsToAdd.forEach((tagIdentifier) => {
        next.delete(tagIdentifier);
      });
      tagIdsToOverwrite.forEach((tagIdentifier) => {
        next.delete(tagIdentifier);
      });
      return next;
    });
    setSelectedHubTags(new Set());
  }, [
    setUserTags,
    tagIdentifierMap,
    selectedHubTags,
    distro,
    collisionMode,
    setDirtyTags,
  ]);

  const handleSelectedColumnChange = (_, newValue) => {
    setSelectedColumns(newValue);
  };

  const searchForHubTags = useCallback(
    async ({ assetNumber, assetId }) => {
      try {
        setAssetLoading(true);
        setErrorMessage("");
        const newTagIdentifierMap = new Map();
        const response = await getAssetTagsFromHub(assetNumber, assetId);
        // Pair the tags from hub with the current tags, matching on TagIdentifier
        response.data.tags.forEach((tagFromHub) => {
          const userTag = userTags.find(
            (t) => t.TagIdentifier === tagFromHub.TagIdentifier
          );
          newTagIdentifierMap.set(tagFromHub.TagIdentifier, {
            userTag: userTag,
            tagFromHub: tagFromHub,
          });
        });
        setTagIdentifierMap(newTagIdentifierMap);
        setSelectedHubTags(new Set());
        setLastAssetNumberSearched(
          assetId ? `${assetNumber} (asset id ${assetId})` : assetNumber
        );
      } catch (error) {
        setTagIdentifierMap(new Map());
        setSelectedHubTags(new Set());
        setLastAssetNumberSearched("");
        const apiErrorMessage = extractApiErrorMessage(error, "");
        const detail = apiErrorMessage
          ? `Could not load tags from the hub for this asset. ${apiErrorMessage}`
          : "Could not load tags from the hub for this asset.";
        setErrorMessage(detail);
      } finally {
        setAssetLoading(false);
      }
    },
    [userTags]
  );

  const getCellStyle = useCallback(
    ({ columnId, row }) => {
      // Returns the style for the cell. If there's no matching user tag, return undefined, else check to see if cell should be red
      if (columnId === SelectColumnId) {
        return undefined;
      }
      const { userTag, tagFromHub } =
        tagIdentifierMap?.get(row.TagIdentifier) ?? {};
      if (userTag == null || tagFromHub == null) {
        return undefined;
      }

      // Return red if the column is one of the static columns
      if (leftColumns.some((column) => column.id === columnId)) {
        return { color: conflictColor };
      }

      // We are left with an optional column. Check to see if the value is different.
      const isValueDifferent = isValueDifferentForOptionalColumn(
        userTag,
        tagFromHub,
        columnId,
        distro
      );
      if (isValueDifferent) {
        return { color: conflictColor };
      }
      return undefined;
    },
    [tagIdentifierMap, leftColumns, distro]
  );

  return (
    <DragModal
      maxWidth='xl'
      title='tagComparison'
      onClose={() => setShowTagComparisonDialog(false)}
      open={showTagComparisonDialog}
      fullWidth
      titlecomponent={
        <Typography variant='h5' color='primary'>
          Tag Comparison
        </Typography>
      }>
      <Divider />
      <DialogContent
        style={{
          height: "80vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}>
        <div style={{ margin: 0, paddingLeft: "16px", fontSize: "1rem" }}>
          Search for Hub
        </div>
        <div
          style={{
            display: "flex",
            gap: "20px",
            justifyContent: "space-between",
          }}>
          <SearchAssetNumber onSearch={searchForHubTags} />
          <span
            style={{
              display: "flex",
              gap: "20px",
              alignItems: "center",
              flexWrap: "wrap",
            }}>
            <SelectCollisionMode
              collisionMode={collisionMode}
              setCollisionMode={setCollisionMode}
            />
            <SelectDistro distro={distro} setDistro={setDistro} />
            <Autocomplete
              multiple
              limitTags={1}
              options={optionalColumns}
              getOptionLabel={(option) =>
                optionalColumnLabels[option] ?? option
              }
              onChange={handleSelectedColumnChange}
              renderInput={(params) => (
                <TextField {...params} label='Columns' placeholder='Column' />
              )}
              renderOption={(props, option, { selected }) => {
                const { key, ...optionProps } = props;
                return (
                  <li key={key} {...optionProps}>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {optionalColumnLabels[option] ?? option}
                  </li>
                );
              }}
              renderTags={() => {}}
              disableCloseOnSelect
              size='small'
              value={selectedColumns}
              sx={{ width: 250 }}
              disableListWrap
            />
          </span>
        </div>
        {!!infoMessage && (
          <Alert
            severity='info'
            sx={{ my: 2 }}
            onClose={() => setInfoMessage("")}>
            {infoMessage}
          </Alert>
        )}
        {!!errorMessage && (
          <Alert
            severity='error'
            sx={{ my: 2 }}
            onClose={() => setErrorMessage("")}>
            {errorMessage}
          </Alert>
        )}
        <Divider sx={{ mt: 1, mb: 2 }} />
        <div style={{ display: "flex", gap: "20px", height: "80%" }}>
          <span style={{ overflow: "auto", flexBasis: "50%" }}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography component='h3' variant='h6' sx={{ m: 0 }}>
                  Current Tags
                </Typography>
                <Tooltip title={USER_FILTER_TOOLTIPS[userTableFilterMode]}>
                  <IconButton
                    size='small'
                    aria-pressed={
                      userTableFilterMode !== TagTableFilterMode.All
                    }
                    aria-label='Cycle filter: all, conflicts only, no conflicts'
                    style={{
                      color:
                        userTableFilterMode === TagTableFilterMode.ConflictsOnly
                          ? conflictColor
                          : userTableFilterMode ===
                              TagTableFilterMode.NoConflictsOnly
                            ? noConflictColor
                            : undefined,
                    }}
                    onClick={() => setUserTableFilterMode((m) => (m + 1) % 3)}>
                    <FilterList fontSize='small' />
                  </IconButton>
                </Tooltip>
              </Box>
              <Paper
                elevation={2}
                sx={{ p: 2, mt: 1 }}
                style={{ minHeight: 0, display: "flex", padding: "2px" }}>
                <DataGrid
                  rows={userTagsForGrid}
                  columns={columns}
                  alternateRowColors={true}
                  getCellStyle={getCellStyle}
                />
              </Paper>
            </div>
          </span>
          <span style={{ overflow: "auto", flexBasis: "50%" }}>
            <Backdrop
              open={assetLoading}
              sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}>
              <CircularProgress />
            </Backdrop>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  flexWrap: "wrap",
                }}>
                <Typography component='h3' variant='h6' sx={{ m: 0 }}>
                  Tags from Hub
                  {lastAssetNumberSearched
                    ? ` - ${lastAssetNumberSearched}`
                    : ""}
                </Typography>
                <Tooltip title={HUB_FILTER_TOOLTIPS[hubTableFilterMode]}>
                  <IconButton
                    size='small'
                    aria-pressed={hubTableFilterMode !== TagTableFilterMode.All}
                    aria-label='Cycle filter: all, conflicts only, no conflicts'
                    style={{
                      color:
                        hubTableFilterMode === TagTableFilterMode.ConflictsOnly
                          ? conflictColor
                          : hubTableFilterMode ===
                              TagTableFilterMode.NoConflictsOnly
                            ? noConflictColor
                            : undefined,
                    }}
                    onClick={() => setHubTableFilterMode((m) => (m + 1) % 3)}>
                    <FilterList fontSize='small' />
                  </IconButton>
                </Tooltip>
              </Box>
              <Paper
                elevation={2}
                sx={{ p: 2, mt: 1 }}
                style={{ minHeight: 0, display: "flex", padding: "2px" }}>
                <DataGrid
                  rows={hubTagRows ?? []}
                  columns={columns}
                  alternateRowColors={true}
                  selectColumn
                  selectedRows={selectedHubTags}
                  onSelectedRowsChange={setSelectedHubTags}
                  getRowId={(row) => row.TagIdentifier}
                  getCellStyle={getCellStyle}
                />
              </Paper>
            </div>
          </span>
        </div>
        <DialogActions>
          <AddTagsFromHubConfirmation
            tagIdentifierMap={tagIdentifierMap}
            distro={distro}
            lookupValues={lookupValues}
            collisionMode={collisionMode}
            commitAddSelectedTags={commitAddSelectedTags}
            selectedHubTags={selectedHubTags}
            buttonDisabled={
              selectedHubTags.size === 0 ||
              collisionMode === CollisionMode.NoneSet
            }
          />
        </DialogActions>
      </DialogContent>
    </DragModal>
  );
}
